/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function, max-statements */
import { ApolloError, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import prepareComponent from "@bluframe/grapple/prepareComponent";
import useToggle from "@bluframe/grapple/useToggle";
import { mutations, queries } from "@bluframe/mightychroma-mightytool-graphql-schema";
import useAutosuggestOptions from "hooks/useAutosuggestOptions";
import useInferLabelCreditByUrl from "hooks/useInferLabelCreditByUrl";
import useRegenerateScreenshotMetadataField from "hooks/useRegenerateScreenshotMetadataField";
import useUser from "hooks/useUser";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Item from "./Item";
const GUARDRAIL_DEPENDENCY_KEYS = [
    "dependencies",
    "blockedDependencies",
    "dependencyEntities",
    "dependencyItems",
    "linkedItems"
];
const normalizeDependencyType = (type) => type
    .replace(/([a-z0-9])([A-Z])/gu, "$1_$2")
    .replace(/[\s-]+/gu, "_")
    .toUpperCase();
const toGuardrailDependency = (dependency, fallbackType) => {
    const id = dependency.id ??
        dependency.homeVideoId ??
        dependency.homeVideoReviewId ??
        dependency.newsId ??
        dependency.entityId;
    if (!id) {
        return null;
    }
    const title = dependency.title ??
        dependency.name ??
        dependency.headline ??
        dependency.slug ??
        `Linked ${id}`;
    const type = normalizeDependencyType(String(dependency.type ??
        dependency.entityType ??
        dependency.kind ??
        fallbackType ??
        "LINKED"));
    return {
        id: String(id),
        title: String(title),
        type
    };
};
const normalizeGuardrailDependencies = (raw) => {
    if (!raw) {
        return [];
    }
    if (Array.isArray(raw)) {
        return raw
            .map((item) => item && typeof item === "object"
            ? toGuardrailDependency(item)
            : null)
            .filter(Boolean);
    }
    if (typeof raw === "object") {
        return Object.entries(raw).flatMap(([typeKey, value]) => {
            if (!Array.isArray(value)) {
                return [];
            }
            return value
                .map((item) => item && typeof item === "object"
                ? toGuardrailDependency(item, typeKey)
                : null)
                .filter(Boolean);
        });
    }
    return [];
};
const getGuardrailStateFromError = (error) => {
    if (!(error instanceof ApolloError)) {
        return null;
    }
    for (const graphQLError of error.graphQLErrors) {
        const extensions = (graphQLError.extensions ??
            {});
        const directDependencies = GUARDRAIL_DEPENDENCY_KEYS.reduce((acc, key) => {
            const value = extensions[key];
            if (value) {
                acc.push(...normalizeGuardrailDependencies(value));
            }
            return acc;
        }, []);
        const nestedDependencies = [
            extensions.data?.dependencies,
            extensions.details?.dependencies,
            extensions.exception?.data?.dependencies
        ]
            .filter(Boolean)
            .flatMap((value) => normalizeGuardrailDependencies(value));
        const dependencies = [...directDependencies, ...nestedDependencies];
        if (dependencies.length) {
            const message = typeof extensions.userMessage === "string"
                ? extensions.userMessage
                : graphQLError.message;
            return {
                dependencies,
                message
            };
        }
    }
    return null;
};
const { homeVideos: { GET_HOME_VIDEOS, GET_HOME_VIDEO_BY_ID }, vendors: { SEARCH_LABELS, SEARCH_STORES } } = queries;
const { homeVideos: { DELETE_HOME_VIDEO, UPDATE_HOME_VIDEO } } = mutations;
const usePrepareComponent = () => {
    const [isEditing, toggleIsEditing] = useToggle();
    const history = useHistory();
    const params = useParams();
    const [selectedLabelId, setSelectedLabelId] = useState();
    const [selectedStoreIds, setSelectedStoreIds] = useState([]);
    const onInferScreenshotCredit = useInferLabelCreditByUrl();
    const { onRegenerateScreenshotMetadataField } = useRegenerateScreenshotMetadataField();
    const [deleteGuardrailState, setDeleteGuardrailState] = useState(null);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const { isAdmin, isEditor } = useUser();
    const { data, loading } = useQuery(GET_HOME_VIDEO_BY_ID, {
        fetchPolicy: "network-only",
        variables: { id: params.id }
    });
    const [updateHomeVideo, { loading: isSaveLoading }] = useMutation(UPDATE_HOME_VIDEO, {
        fetchPolicy: "network-only",
        refetchQueries: [
            { query: GET_HOME_VIDEO_BY_ID, variables: { id: params.id } }
        ]
    });
    const [deleteHomeVideo] = useMutation(DELETE_HOME_VIDEO, {
        refetchQueries: [{ query: GET_HOME_VIDEOS }]
    });
    useEffect(() => {
        const labelId = data?.getHomeVideoById?.label?.id;
        setSelectedLabelId((previous) => previous === labelId ? previous : labelId);
    }, [data?.getHomeVideoById?.label?.id]);
    const [searchLabels, { data: searchLabelsData, loading: labelsSuggestionsLoading }] = useLazyQuery(SEARCH_LABELS);
    const [searchStores, { data: searchStoresData, loading: searchStoresLoading }] = useLazyQuery(SEARCH_STORES);
    useEffect(() => {
        if (!data?.getHomeVideoById?.storeLinks)
            return;
        // Build an array of { id, index } for each preexisting storeLink
        setSelectedStoreIds(data.getHomeVideoById.storeLinks.map((storeLink, index) => ({
            id: storeLink.store.id,
            index
        })));
    }, [data?.getHomeVideoById?.storeLinks]);
    /**
     * Cancels editing of home video
     */
    const onCancel = useCallback(() => {
        toggleIsEditing();
    }, [toggleIsEditing]);
    /**
     * Selects a label by ID
     *
     * @param id
     */
    const onSelectLabelId = useCallback((id) => {
        setSelectedLabelId(id);
    }, [setSelectedLabelId]);
    /**
     * Selects a store by ID
     *
     * @param index
     */
    const onSelectStoreId = useCallback((index) => (id) => {
        /**
         * TODO: Maybe we can do something nice to ensure uniqueness of one
         * store per link? Do we even want to?
         * If we do we also want the dropdowns to only present stores that
         * we haven't selected
         * And what if we need 2 or more of the same store?
         */
        setSelectedStoreIds((prev) => {
            /**
             * Find index of store link with same index
             */
            const presentIndex = prev.findIndex((item) => item.index === index);
            /**
             * If we have a store link with the same index, we update it
             */
            if (presentIndex >= 0) {
                const next = prev.slice();
                next[presentIndex] = { id, index };
                return next;
            }
            /**
             * If we don't have a store link with same index
             * we add a new one
             */
            return [...prev, { id, index }];
        });
    }, [setSelectedStoreIds]);
    /**
     * Label suggestions for autosuggest input.
     *
     * @param {{ inputValue: string }} root0
     * @param {string} root0.inputValue - The label name to search for
     */
    const onGetLabelsSuggestions = useCallback(({ inputValue }) => {
        searchLabels({
            variables: {
                searchLabelsInput: { name: inputValue }
            }
        });
    }, [searchLabels]);
    /**
     * Store suggestions for autosuggest input.
     *
     * @param {{ inputValue: string }} root0
     * @param {string} root0.inputValue - The URL of the store to search for
     */
    const onGetStoresSuggestions = useCallback(({ inputValue }) => {
        searchStores({
            variables: {
                searchStoresInput: {
                    url: inputValue
                }
            }
        });
    }, [searchStores]);
    /**
     * Enables editing of home video
     */
    const onEdit = useCallback(() => {
        toggleIsEditing();
    }, [toggleIsEditing]);
    /**
     * Publishes or unpublishes home video
     */
    const onPublishToggle = useCallback(() => {
        updateHomeVideo({
            variables: {
                homeVideoUpdateInput: {
                    isPublished: !data?.getHomeVideoById.isPublished
                },
                id: params.id
            }
        });
    }, [data?.getHomeVideoById.isPublished, params.id, updateHomeVideo]);
    /**
     * Saves home video and store links.
     *
     * @param homeVideoUpdate
     * @param storeLinksUpdate
     */
    const onSave = useCallback((homeVideoUpdate, storeLinksUpdate) => {
        if (!params.id || !homeVideoUpdate.name) {
            return;
        }
        updateHomeVideo({
            onCompleted: () => toggleIsEditing(),
            variables: {
                homeVideoUpdateInput: homeVideoUpdate,
                id: params.id,
                storeLinksUpdateInput: storeLinksUpdate
            }
        });
    }, [params.id, updateHomeVideo, toggleIsEditing]);
    /**
     * Delete home video
     */
    const onDelete = useCallback(async () => {
        if (!params.id) {
            return;
        }
        setIsDeleteLoading(true);
        setDeleteGuardrailState(null);
        try {
            await deleteHomeVideo({
                variables: { id: params.id }
            });
            history.push("/home-videos/home-videos");
        }
        catch (error) {
            const guardrailState = getGuardrailStateFromError(error);
            if (guardrailState) {
                setDeleteGuardrailState(guardrailState);
            }
            else {
                // eslint-disable-next-line no-console
                console.error("Failed to delete home video", error);
            }
        }
        finally {
            setIsDeleteLoading(false);
        }
    }, [deleteHomeVideo, history, params.id]);
    const onDeleteGuardrailDismiss = useCallback(() => {
        setDeleteGuardrailState(null);
    }, []);
    /**
     * Generates options for the label autosuggest input.
     */
    const autosuggestLabels = useAutosuggestOptions({
        selected: data?.getHomeVideoById?.label
            ? [
                {
                    id: data.getHomeVideoById.label.id,
                    name: data.getHomeVideoById.label.vendor.name
                }
            ]
            : [],
        suggestions: searchLabelsData?.searchLabels.map((item) => ({
            id: item.id,
            name: item.vendor.name
        })) ?? []
    });
    /**
     * Generates options for the vendor autosuggest input.
     */
    const storeOptionsById = useMemo(() => {
        const options = {};
        data?.getHomeVideoById?.storeLinks?.forEach((storeLink) => {
            const { store } = storeLink;
            if (!store?.id) {
                return;
            }
            options[store.id] = {
                id: store.id,
                name: store.url ?? ""
            };
        });
        return options;
    }, [data?.getHomeVideoById?.storeLinks]);
    const autosuggestStores = useMemo(() => {
        const suggestions = searchStoresData?.searchStores?.map((item) => ({
            id: item.id,
            name: item.url ?? ""
        })) ?? [];
        return suggestions;
    }, [searchStoresData?.searchStores]);
    return {
        deleteGuardrailDependencies: deleteGuardrailState?.dependencies,
        deleteGuardrailMessage: deleteGuardrailState?.message,
        isAdmin,
        isEditing,
        isEditor,
        isDeleteLoading,
        isLabelsSuggestionsLoading: labelsSuggestionsLoading,
        isSaveLoading,
        isStoresSuggestionsLoading: searchStoresLoading,
        item: data?.getHomeVideoById,
        labels: autosuggestLabels,
        loading,
        onCancel,
        onDelete,
        onEdit,
        onGetLabelsSuggestions,
        onGetStoresSuggestions,
        onInferScreenshotCredit,
        onRegenerateScreenshotMetadataField,
        onDeleteGuardrailDismiss,
        onPublishToggle,
        onSave,
        onSelectLabelId,
        onSelectStoreId,
        shouldCloseDeleteModalOnConfirm: false,
        selectedLabelId,
        selectedStoreIds,
        storeOptionsById,
        stores: autosuggestStores
    };
};
export default prepareComponent(usePrepareComponent)(Item);
